<template>
  <div
    class="c-my-data-sharing-item"
    :class="{ 'c-my-data-sharing-item--modified': hasChanges }"
  >
    <!-- Column 1: Population name (3fr) -->
    <div class="c-my-data-sharing-item__population">
      <BittsAvatar
        v-if="population.integration_type"
        size="small"
        shape="square"
        :show-border="true"
        :custom-svg="
          population.integration_type
            ? `${population.integration_type}Icon`
            : ''
        "
        class="c-my-data-sharing-item__integration-avatar"
      />
      <component
        :is="shouldShowNameTooltip ? BittsTooltip : 'div'"
        class="min-w-0"
      >
        <h3 ref="nameRef" class="font-bold truncate min-w-0">
          {{ population.name }}
        </h3>
        <template v-if="shouldShowNameTooltip" #title>
          {{ population.name }}
        </template>
      </component>
      <BittsTag
        v-if="population.standard_type"
        size="x-small"
        color="info"
        text="Standard"
      />
    </div>

    <!-- Column 2: Sharing level (2fr) -->
    <div class="c-my-data-sharing-item__sharing-level">
      <SharingSettingsVisibilitySelector
        :initial-visibility="currentVisibility"
        :disabled="!hasSharingPermission"
        :is-greenfield-sharing-blocked="isGreenfieldSharingBlocked"
        :disabled-option-content="{
          text: 'This sharing option was disabled. Please reach out to your admins.',
        }"
        @change="handleVisibilityChange"
      />
    </div>

    <!-- Column 3: Field presets (2fr) -->
    <div class="c-my-data-sharing-item__shared-fields">
      <SharingSettingsPresetSelector
        v-if="isShared"
        :initial-selected-preset-id="currentPresetId"
        :preset-options="presetOptionsWithCustomFields"
        :disabled="!hasSharingPermission"
        :can-edit-presets="false"
        :feed-id="presetParentId"
        :is-compact="true"
        @change="handlePresetChange"
        @open-preset-modal="handleOpenPresetModal"
      />
    </div>

    <!-- Column 4: Actions menu (1fr) -->
    <div class="c-my-data-sharing-item__actions">
      <template v-if="hasSharingPermission">
        <!-- Reset to Default button (shown when there's a saved partner override) -->
        <BittsTooltip v-if="isCustom" placement="top">
          <BittsButton
            aria-label="Restore default sharing settings"
            :center-icon="['fak', 'restore']"
            size="x-small"
            type="neutral"
            variant="outline"
            @click="handleRestoreToDefault"
          />
          <template #title>Restore default sharing settings</template>
        </BittsTooltip>
        <!-- Ellipsis dropdown menu (only show if there are options) -->
        <BittsDropdown
          v-if="dropdownOptions.length > 0"
          :options="dropdownOptions"
          placement="bottomRight"
          @selection="onOptionSelected"
        >
          <template #prompt>
            <BittsButton
              aria-label="More options"
              :center-icon="['fas', 'ellipsis']"
              size="x-small"
              type="neutral"
              variant="outline"
            />
          </template>
        </BittsDropdown>
      </template>
      <BittsButton
        v-else
        aria-label="View sharing details"
        class="c-my-data-sharing-item__preset-details-btn"
        variant="ghost"
        type="neutral"
        size="small"
        :center-icon="['far', 'eye']"
        @click="handleOpenPresetModal(Number(currentPresetId))"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  BittsAvatar,
  BittsButton,
  BittsDropdown,
  BittsTag,
  BittsTooltip,
} from '@crossbeam/bitts';
import { BittsIconPrefix } from '@crossbeam/bitts/src/types';

import { useResizeObserver } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

import SharingSettingsPresetSelector from '@/components/sharing-settings/SharingSettingsPresetSelector.vue';
import SharingSettingsVisibilitySelector from '@/components/sharing-settings/SharingSettingsVisibilitySelector.vue';

import useAuth from '@/composables/useAuth';
import { useFieldPresetsModal } from '@/composables/useFieldPresetsModal';
import { SHARING_TYPES, SharingType } from '@/constants/data_shares';
import { isGoogleOrCsvSource } from '@/constants/data_sources';
import { SHARING_OVERRIDES_ENABLED } from '@/constants/feature_flags';
import {
  useDataSharesStore,
  useFeatureFlagStore,
  usePopulationsStore,
} from '@/stores';
import { DataSharePreset } from '@/types/data_share_presets';
import type { PresetOptionsGroup } from '@/types/data_share_presets';
import type { Population } from '@/types/populations';

import {
  DataShareItem,
  PopulationToPopulationMap,
} from '../composables/useSharingSettings';

type DropdownOption = {
  value: string;
  display: string;
  classes?: string[];
  disabled?: boolean;
  tooltip?: string;
  iconType?: [BittsIconPrefix, string];
};

type Props = {
  population: Population;
  partnerId: number;
  dataShare?: DataShareItem;
  presetOptions?: PresetOptionsGroup[];
  singleSharePresets: DataSharePreset[];
  createDataShare: (params: {
    populationId: number;
    visibility: SharingType;
    presetId?: number | null;
  }) => void;
  updateDataShare: (params: {
    populationId: number;
    visibility: SharingType;
    presetId?: number | null;
  }) => void;
  resetDataShare: (dataShare: DataShareItem) => void;
  deleteOverride: (
    shareId: number | null,
    partnerPopulationId: number,
    populationId: number,
  ) => void;
  currentPopulationToPopulation: PopulationToPopulationMap;
  hasChanges?: boolean;
  isCustom?: boolean;
};

const props = defineProps<Props>();

const emit = defineEmits<(e: 'show-pop-overrides-modal') => void>();

const { hasPermission } = useAuth();

const { openModal } = useFieldPresetsModal();

const nameRef = ref<HTMLElement | null>(null);
const shouldShowNameTooltip = ref(false);

function checkTruncation() {
  if (!nameRef.value) return;
  shouldShowNameTooltip.value =
    nameRef.value.scrollWidth > nameRef.value.clientWidth;
}

useResizeObserver(nameRef, checkTruncation);

const ffStore = useFeatureFlagStore();
const hasSharingOverrides = computed(() =>
  ffStore.hasFeatureFlag(SHARING_OVERRIDES_ENABLED),
);

const dataSharesStore = useDataSharesStore();
const { isGreenfieldSharingBlocked } = storeToRefs(dataSharesStore);

const hasSharingPermission = computed(() => hasPermission('manage:sharing'));

// Current state (defaults to HIDDEN if no dataShare)
const currentVisibility = computed(
  () => props.dataShare?.visibility ?? SHARING_TYPES.HIDDEN,
);

const currentPresetId = computed(() => props.dataShare?.presetId ?? null);

const isShared = computed(() => {
  const sharingTypes: SharingType[] = [
    SHARING_TYPES.SHARING,
    SHARING_TYPES.GREENFIELD_SHARING,
  ];
  return sharingTypes.includes(currentVisibility.value);
});

const presetParentId = computed(() => {
  const isGoogleOrCsv = isGoogleOrCsvSource(props.population.integration_type);
  return isGoogleOrCsv ? props.population.source_id : props.population.feed_id;
});

const hasExistingOverrides = computed(() => {
  const overrides = props.currentPopulationToPopulation[props.population.id];
  return Boolean(overrides && Object.keys(overrides).length > 0);
});

const isDefaultSettings = computed(() => {
  return !props.isCustom;
});

const selectedSingleSharePreset = computed(() =>
  props.singleSharePresets.find(
    (preset) => preset.id === currentPresetId.value,
  ),
);

const presetOptionsWithCustomFields = computed(() => {
  const baseOptions = props.presetOptions ?? [];

  if (!selectedSingleSharePreset.value) return baseOptions;

  return [
    ...baseOptions,
    {
      heading: null,
      items: [
        {
          value: selectedSingleSharePreset.value.id,
          label: 'Custom Field Selection',
          presetType: 'single_share_preset',
        },
      ],
    },
  ];
});

const { partnerPopulations } = storeToRefs(usePopulationsStore());

const dropdownOptions = computed(() => {
  const options: DropdownOption[] = [];

  // Only show dropdown options when FF is enabled
  if (!hasSharingOverrides.value) return options;

  // Don't show dropdown options when population is hidden
  if (currentVisibility.value === SHARING_TYPES.HIDDEN) return options;

  // Determine display text and icon based on whether overrides exist
  const overrideDisplayText = hasExistingOverrides.value
    ? 'Edit overrides'
    : 'Create an override';
  const overrideIconType = hasExistingOverrides.value ? 'edit' : 'add';

  // Show override option (Create/Edit)
  options.push({
    value: 'create-override',
    display: overrideDisplayText,
    classes: ['c-my-data-sharing-item__container-option'],
    iconType: ['fak', overrideIconType],
    disabled: !partnerPopulations.value.length,
  });

  // Show "Remove all overrides" option when there are existing overrides
  if (hasExistingOverrides.value) {
    options.push({
      value: 'remove-overrides',
      display: 'Remove all overrides',
      classes: ['c-my-data-sharing-item__container-option'],
      iconType: ['fak', 'delete'],
    });
  }

  return options;
});

function onOptionSelected(option: { value: string }) {
  if (option.value === 'create-override') {
    onCreateOverride();
  } else if (option.value === 'remove-overrides') {
    handleRemoveAllOverrides();
  }
}

function handleVisibilityChange(visibility: SharingType) {
  const hasDataShare = !!props.dataShare;

  if (visibility === SHARING_TYPES.HIDDEN) {
    // Set to hidden
    if (hasDataShare) {
      props.updateDataShare({
        populationId: props.population.id,
        visibility: SHARING_TYPES.HIDDEN,
        presetId: currentPresetId.value,
      });
    }
    // If no dataShare exists, it's already HIDDEN
    return;
  }

  // Share the population
  if (hasDataShare && !isDefaultSettings.value) {
    // Update existing partner override
    props.updateDataShare({
      populationId: props.population.id,
      visibility,
      presetId: currentPresetId.value,
    });
  } else {
    // Create new partner override
    props.createDataShare({
      populationId: props.population.id,
      visibility,
      presetId: currentPresetId.value,
    });
  }
}

function handlePresetChange(presetId: number) {
  const hasDataShare = !!props.dataShare;

  if (hasDataShare && !isDefaultSettings.value) {
    props.updateDataShare({
      populationId: props.population.id,
      visibility: currentVisibility.value,
      presetId,
    });
  } else {
    props.createDataShare({
      populationId: props.population.id,
      visibility: currentVisibility.value,
      presetId,
    });
  }
}

function onCreateOverride() {
  emit('show-pop-overrides-modal');
}

const sourceIdParam = isGoogleOrCsvSource(
  String(props.population.integration_type),
)
  ? props.population.source_id
  : undefined;

function handleOpenPresetModal(presetId: number | undefined) {
  openModal({
    feedId: props.population.feed_id,
    sourceId: sourceIdParam,
    presetId,
    isForPartner: true,
    onSuccess: (newPresetId: number, _isNewPreset: boolean) => {
      handlePresetChange(newPresetId);
    },
  });
}

function handleRestoreToDefault() {
  if (props.dataShare) {
    props.resetDataShare(props.dataShare);
    return;
  }

  const sessionKey = dataSharesStore.getSessionKey(props.partnerId);
  const initialDataShare = dataSharesStore.initialEditingDataShares[
    sessionKey
  ]?.find((ds) => ds.populationId === props.population.id);

  if (initialDataShare) {
    props.resetDataShare(initialDataShare);
    return;
  }

  const hiddenPartnerRule = dataSharesStore.outgoingSharingRules.find(
    (rule) =>
      rule.population_id === props.population.id &&
      rule.partner_org_id === props.partnerId &&
      rule.rule_type === 'partner',
  );

  if (hiddenPartnerRule) {
    props.resetDataShare(
      dataSharesStore.toDataShareItem(hiddenPartnerRule, [props.population]),
    );
  }
}

function handleRemoveAllOverrides() {
  const overrides = props.currentPopulationToPopulation[props.population.id];
  if (!overrides) return;

  // Get the partner population rules from the store
  const partnerPopRules =
    dataSharesStore.outgoingSharingRuleMap?.partner_population;

  // Delete all overrides for this population
  Object.keys(overrides).forEach((partnerPopIdStr) => {
    const partnerPopId = Number(partnerPopIdStr);

    // Find the share ID for this override from the store
    let shareId: number | null = null;
    if (partnerPopRules) {
      const rules = partnerPopRules[partnerPopId];
      if (rules) {
        const rule = rules.find(
          (r) =>
            r.population_id === props.population.id &&
            r.partner_org_id === props.partnerId,
        );
        shareId = rule?.id ?? null;
      }
    }

    props.deleteOverride(shareId, partnerPopId, props.population.id);
  });
}
</script>

<style lang="pcss" scoped>
@reference "../../../../../../css/theme.css";
.c-my-data-sharing-item {
  @apply grid gap-16 p-16 items-center transition-colors;
  grid-template-columns: 3fr 2fr 2fr 1fr;
}

.c-my-data-sharing-item--modified {
  background-color: rgba(221, 222, 255, 0.25);
}

.c-my-data-sharing-item__population {
  @apply flex items-center gap-10 min-w-0;
}

.c-my-data-sharing-item__sharing-level {
  @apply flex items-center min-w-0;
}

.c-my-data-sharing-item__shared-fields {
  @apply flex items-center min-w-0;
}

.c-my-data-sharing-item__actions {
  @apply flex items-center justify-end gap-8;
}

.c-my-data-sharing-item__container-option {
  @apply flex w-full items-center px-8 py-4;
}

.c-my-data-sharing-item__container-option:hover {
  @apply bg-neutral-background-weak rounded;
}

:deep(.c-my-data-sharing-item__integration-avatar span) {
  @apply bg-white!;
}
</style>
